import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';

/**
 * ChatStaffPanel
 * Inline panel for staff to chat with a website visitor.
 * Shows conversation history and allows real-time messaging.
 */
function ChatStaffPanel({ practiceId, sessionId, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [session, setSession] = useState(null);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    loadMessages();
    // Poll for new messages every 3 seconds
    pollRef.current = setInterval(loadMessages, 3000);
    return () => clearInterval(pollRef.current);
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = async () => {
    try {
      const res = await api.get(`/practices/${practiceId}/chat/sessions/${sessionId}/messages`);
      if (res.success) {
        setMessages(res.data || []);
      }
    } catch (err) {
      console.error('Load chat messages error:', err);
    }
    setLoading(false);
  };

  const handleTakeover = async () => {
    try {
      const res = await api.post(`/practices/${practiceId}/chat/sessions/${sessionId}/takeover`);
      if (res.success) {
        setSession(res.data);
        loadMessages();
      }
    } catch (err) {
      console.error('Takeover error:', err);
    }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text) return;

    setSending(true);
    try {
      await api.post(`/practices/${practiceId}/chat/sessions/${sessionId}/message`, { content: text });
      setInput('');
      loadMessages();
    } catch (err) {
      console.error('Send message error:', err);
    }
    setSending(false);
  };

  const closeChat = async () => {
    try {
      await api.post(`/practices/${practiceId}/chat/sessions/${sessionId}/close`);
      if (onClose) onClose();
    } catch (err) {
      console.error('Close chat error:', err);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const roleLabel = (role) => {
    switch (role) {
      case 'visitor': return 'Visitor';
      case 'ai': return 'AI';
      case 'staff': return 'Staff';
      case 'system': return 'System';
      default: return role;
    }
  };

  const roleColor = (role) => {
    switch (role) {
      case 'visitor': return { bg: '#dbeafe', text: '#1e40af' };
      case 'ai': return { bg: '#f3f4f6', text: '#374151' };
      case 'staff': return { bg: '#d1fae5', text: '#065f46' };
      case 'system': return { bg: '#fef3c7', text: '#92400e' };
      default: return { bg: '#f3f4f6', text: '#374151' };
    }
  };

  const needsTakeover = messages.length > 0 && !messages.some(m => m.role === 'staff');

  return (
    <div style={panelStyles.container}>
      <div style={panelStyles.header}>
        <div>
          <div style={panelStyles.headerTitle}>Live Chat</div>
          <div style={panelStyles.headerSub}>
            Session #{sessionId}
            {session?.visitor_name && ` - ${session.visitor_name}`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {needsTakeover && (
            <button onClick={handleTakeover} style={panelStyles.takeoverBtn}>
              Take Over
            </button>
          )}
          <button onClick={closeChat} style={panelStyles.closeBtn}>End Chat</button>
          {onClose && (
            <button onClick={onClose} style={panelStyles.minimizeBtn}>Minimize</button>
          )}
        </div>
      </div>

      <div style={panelStyles.messages}>
        {loading ? (
          <div style={{ textAlign: 'center', color: '#9ca3af', padding: '20px' }}>Loading...</div>
        ) : (
          messages.map((msg, i) => {
            const colors = roleColor(msg.role);
            return (
              <div key={msg.id || i} style={{
                ...panelStyles.msgRow,
                justifyContent: msg.role === 'visitor' ? 'flex-end' : 'flex-start'
              }}>
                <div style={{
                  ...panelStyles.msgBubble,
                  background: colors.bg,
                  color: colors.text,
                  ...(msg.role === 'system' ? panelStyles.systemMsg : {})
                }}>
                  {msg.role !== 'system' && (
                    <div style={panelStyles.msgRole}>{roleLabel(msg.role)}</div>
                  )}
                  <div>{msg.content}</div>
                  <div style={panelStyles.msgTime}>
                    {msg.created_at ? new Date(msg.created_at + 'Z').toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <div style={panelStyles.inputArea}>
        <input
          style={panelStyles.input}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a reply..."
          disabled={sending}
        />
        <button
          onClick={sendMessage}
          disabled={!input.trim() || sending}
          style={{
            ...panelStyles.sendBtn,
            opacity: !input.trim() || sending ? 0.5 : 1
          }}
        >
          Send
        </button>
      </div>
    </div>
  );
}

const panelStyles = {
  container: {
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    background: '#fff',
    display: 'flex',
    flexDirection: 'column',
    height: '450px',
    marginTop: '8px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid #e5e7eb',
    background: '#f9fafb',
    borderRadius: '8px 8px 0 0'
  },
  headerTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#111827'
  },
  headerSub: {
    fontSize: '12px',
    color: '#6b7280'
  },
  takeoverBtn: {
    padding: '6px 12px',
    borderRadius: '6px',
    border: 'none',
    background: '#3b82f6',
    color: '#fff',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  closeBtn: {
    padding: '6px 12px',
    borderRadius: '6px',
    border: '1px solid #fca5a5',
    background: '#fff',
    color: '#dc2626',
    fontSize: '12px',
    cursor: 'pointer'
  },
  minimizeBtn: {
    padding: '6px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    background: '#fff',
    color: '#374151',
    fontSize: '12px',
    cursor: 'pointer'
  },
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    background: '#fafafa'
  },
  msgRow: {
    display: 'flex'
  },
  msgBubble: {
    maxWidth: '75%',
    padding: '8px 12px',
    borderRadius: '12px',
    fontSize: '13px',
    lineHeight: '1.4'
  },
  systemMsg: {
    maxWidth: '90%',
    textAlign: 'center',
    alignSelf: 'center',
    fontSize: '12px',
    borderRadius: '6px'
  },
  msgRole: {
    fontSize: '10px',
    fontWeight: '600',
    textTransform: 'uppercase',
    marginBottom: '2px',
    opacity: 0.7
  },
  msgTime: {
    fontSize: '10px',
    opacity: 0.5,
    marginTop: '2px',
    textAlign: 'right'
  },
  inputArea: {
    display: 'flex',
    gap: '8px',
    padding: '12px 16px',
    borderTop: '1px solid #e5e7eb'
  },
  input: {
    flex: 1,
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '20px',
    fontSize: '13px',
    outline: 'none'
  },
  sendBtn: {
    padding: '8px 16px',
    borderRadius: '20px',
    border: 'none',
    background: '#3b82f6',
    color: '#fff',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer'
  }
};

export default ChatStaffPanel;
